import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { loadProjects, deleteProject, newProjectId } from "@/lib/storage";
import { loadSharedProject } from "@/lib/api";
import { summarizeConfiguration } from "@/lib/configurator";
import { FolderOpen, Trash2, Calendar, Star, KeyRound, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const EDITION_LABELS = {
  express: "Express+",
  professional: "Professional+",
  expert: "Expert",
  corporate: "Corporate",
};

export default function Projects({ onLoad, features }) {
  const [projects, setProjects] = useState([]);
  const [codeInput, setCodeInput] = useState("");

  const refresh = () => setProjects(loadProjects());

  useEffect(() => {
    refresh();
    // refresh on storage change (multi-tab)
    const h = () => refresh();
    window.addEventListener("storage", h);
    return () => window.removeEventListener("storage", h);
  }, []);

  const handleDelete = (id, name) => {
    deleteProject(id);
    refresh();
    toast.success(`Projekt „${name}“ gelöscht.`);
  };

  const handleLoadFromCode = async () => {
    const normalized = codeInput.trim().toUpperCase();
    if (!normalized) return;
    try {
      const data = await loadSharedProject(normalized);
      const isConfig = data?.mode === "configurator" || data?.configuration?.mode === "configurator";
      if (!data || (!data.answers && !isConfig)) {
        toast.error("Ungültiger Konfigurationscode.");
        return;
      }
      const project = {
        id: newProjectId(),
        name: data.name || "Aus Code geladen",
        mode: isConfig ? "configurator" : "wizard",
        answers: data.answers || {},
        recommendation: data.recommendation || null,
        configuration: data.configuration || null,
        code: normalized,
      };
      if (onLoad) onLoad(project);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) toast.error("Code nicht gefunden.");
      else if (status === 400) toast.error("Ungültiges Code-Format (6 Zeichen erforderlich).");
      else toast.error("Projekt konnte nicht geladen werden.");
    }
  };

  return (
    <Card className="p-5 md:p-6 border-border bg-card card-elev">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Gespeicherte Projekte</h2>
          <p className="text-sm text-muted-foreground">Ihre Projekte werden lokal im Browser (localStorage) gespeichert.</p>
        </div>
        <Button variant="outline" onClick={refresh} data-testid="btn-refresh-projects">Aktualisieren</Button>
      </div>

      {/* Load from code */}
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <KeyRound className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Projekt aus Konfigurationscode laden</h3>
        </div>
        <p className="text-xs text-muted-foreground mb-3">
          Fügen Sie einen zuvor erzeugten Code ein, um das Projekt wieder zu öffnen – Assistenten-Empfehlungen inklusive Lizenzkonfiguration ebenso wie Konfigurator-Zusammenstellungen.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            data-testid="input-config-code"
            placeholder="ABC123"
            value={codeInput}
            maxLength={6}
            onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/[^A-Z2-9]/g, ""))}
            className="flex-1 font-mono uppercase tracking-[0.35em] text-lg text-center bg-background/60 border-border"
          />
          <Button
            data-testid="btn-load-from-code"
            onClick={handleLoadFromCode}
            disabled={codeInput.trim().length !== 6}
            className="bg-primary text-primary-foreground"
          >
            Projekt öffnen
          </Button>
        </div>
      </div>

      {projects.length === 0 ? (
        <div data-testid="projects-empty" className="text-center py-16 border border-dashed border-border rounded-lg">
          <FolderOpen className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Noch keine Projekte gespeichert.</p>
          <p className="text-xs text-muted-foreground mt-1">Beenden Sie den Assistenten und speichern Sie Ihre Empfehlung.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {projects.map((p) => {
            const ed = p.recommendation?.recommendedEdition;
            const isConfig = p.mode === "configurator";
            const cfgSummary = isConfig ? summarizeConfiguration(p.configuration?.quantities || {}) : null;
            return (
              <div
                key={p.id}
                data-testid={`project-card-${p.id}`}
                className="rounded-lg border border-border bg-secondary/30 p-4 tile-hover hover:border-primary/40"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="font-semibold text-base leading-tight">{p.name}</div>
                  {isConfig ? (
                    <Badge className="bg-primary/15 text-primary border border-primary/40 shrink-0" data-testid={`badge-config-${p.id}`}>
                      <ShoppingCart className="w-3 h-3 mr-1" />
                      Konfigurator
                    </Badge>
                  ) : ed && (
                    <Badge className="bg-primary text-primary-foreground shrink-0">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      {EDITION_LABELS[ed]}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mb-3">
                  <Calendar className="w-3 h-3" />
                  {new Date(p.updatedAt || p.createdAt).toLocaleString("de-DE")}
                  {p.code && <span className="ml-2 font-mono text-primary">· {p.code}</span>}
                </div>
                {isConfig && cfgSummary && (
                  <div className="grid grid-cols-3 gap-1 text-[10px] mb-3">
                    <div className="text-center rounded bg-background/50 border border-border py-1">
                      <div className="text-muted-foreground truncate">Editionen</div>
                      <div className="font-bold truncate">{cfgSummary.totals.editions.map((e) => EDITION_LABELS[e]).join(", ") || "—"}</div>
                    </div>
                    <div className="text-center rounded bg-background/50 border border-border py-1">
                      <div className="text-muted-foreground truncate">Devices</div>
                      <div className="font-bold">{cfgSummary.totals.devices}</div>
                    </div>
                    <div className="text-center rounded bg-background/50 border border-border py-1">
                      <div className="text-muted-foreground truncate">Care</div>
                      <div className="font-bold">{cfgSummary.totals.care}</div>
                    </div>
                  </div>
                )}
                {!isConfig && p.recommendation?.scores && (
                  <div className="grid grid-cols-4 gap-1 text-[10px] mb-3">
                    {Object.entries(p.recommendation.scores).map(([k, v]) => (
                      <div key={k} className="text-center rounded bg-background/50 border border-border py-1">
                        <div className="text-muted-foreground truncate">{EDITION_LABELS[k]}</div>
                        <div className="font-bold">{v}%</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button
                    data-testid={`btn-load-${p.id}`}
                    size="sm"
                    onClick={() => onLoad && onLoad(p)}
                    className="flex-1 bg-primary text-primary-foreground"
                  >
                    Öffnen
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid={`btn-delete-${p.id}`} size="sm" variant="outline" className="text-rose-500 border-rose-500/40 hover:bg-rose-500/10">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Projekt löschen?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Das Projekt „{p.name}“ wird unwiderruflich aus dem lokalen Speicher entfernt.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                        <AlertDialogAction
                          data-testid={`btn-confirm-delete-${p.id}`}
                          onClick={() => handleDelete(p.id, p.name)}
                          className="bg-destructive text-destructive-foreground"
                        >
                          Löschen
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
